import React, { useState } from 'react';
import { Badge, cx } from '../../../ui';
import type { CityCache, GeoState, RegionSelection, SelectionChange } from '../../../types';
import { citiesOfState, stateStatus } from '../regionsModel';
import { CityPicker } from './CityPicker';

/** Accordion of states; each opens its CityPicker. Works for any country, and without the map. */
interface RegionsListViewProps {
  states: GeoState[];
  selection: RegionSelection;
  onChange: SelectionChange;
  cities: CityCache;
}

export function RegionsListView({ states, selection, onChange, cities }: RegionsListViewProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (state: GeoState) => {
    if (openId === state.id) { setOpenId(null); return; }
    setOpenId(state.id);
    cities.ensure(state);
  };

  return (
    <ul className="divide-y divide-line rounded-lg border border-line">
      {states.map((state) => {
        const open = openId === state.id;
        const status = stateStatus(selection, state);
        const picked = citiesOfState(selection, state).length;
        const panelId = `state-panel-${state.id}`;
        return (
          <li key={state.id}>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggle(state)}
              className={cx('flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold hover:bg-canvas', status !== 'none' && 'text-brand-800')}
            >
              <span>{state.name}</span>
              <span className="flex items-center gap-3">
                {status === 'whole' && <Badge tone="brand">Entire state</Badge>}
                {status === 'partial' && <Badge tone="brand">{picked} selected</Badge>}
                <span aria-hidden="true" className="text-muted">{open ? '▴' : '▾'}</span>
              </span>
            </button>
            {open && (
              <div id={panelId} className="border-t border-line bg-canvas px-4 py-3">
                <CityPicker state={state} entry={cities.cache[state.id]} selection={selection} onChange={onChange} onRetry={() => cities.reload(state)} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
